import { Plus } from "lucide-react";
import { QrMedia } from "../public/qr/QrMedia";
import { Quantity, Veg } from "../public/qr/QrMenu";
import { money } from "./orderApi";

export const itemPrice = (it) => (it.variants?.length ? Math.min(...it.variants.map((v) => v.price)) : it.base_price);
export const itemOffer = (it) => (it.offer_price != null && !it.variants?.length ? it.offer_price : null);
export const hasChoices = (it) => !!(it.variants?.length || it.addons?.length || it.is_combo);
export const pctOff = (price, offer) => (price > 0 && offer != null && offer < price ? Math.round(((price - offer) / price) * 100) : null);

export const Price = ({ it, testId }) => {
  const price = itemPrice(it), offer = itemOffer(it), pct = pctOff(price, offer);
  return (
    <div className="os-price" data-testid={testId}>
      {it.variants?.length > 0 && <small>From</small>}
      {offer != null ? <><s>{money(price)}</s><span>{money(offer)}</span>{pct ? <span className="os-badge">{pct}% OFF</span> : null}</> : <span>{money(price)}</span>}
    </div>
  );
};

/** Add control shared by every layout: variants/add-ons → sheet; plain items → direct add; qty stepper when already in cart. */
export const AddControl = ({ it, qty, onOpen, onAdd, onQty, onCart, canOrder }) => {
  if (!it.is_available) return <span className="os-add ghost" data-testid={`menu-item-unavailable-${it.id}`}>{it.unavailable_reason === "out_of_stock" ? "Out of stock" : "Unavailable"}</span>;
  if (!canOrder) return <button type="button" className="os-add ghost" onClick={() => onOpen(it)} data-testid={`menu-add-${it.id}`}>View</button>;
  if (qty > 0) return <Quantity id={`menu-item-${it.id}`} value={qty} minus={() => onQty(it, -1, onCart)} plus={() => (hasChoices(it) ? onCart() : onQty(it, 1))} />;
  return <button type="button" className="os-add" onClick={() => (hasChoices(it) ? onOpen(it) : onAdd(it))} data-testid={`menu-add-${it.id}`}>ADD <Plus size={14} /></button>;
};

/** Compact 2-per-row card (homepage previews, recommendations). */
/** Public aggregate only (avg + count) — present only when the owner enabled it for this branch AND the item has ratings. */
export const Stars = ({ rating, id, size = "" }) => (rating && rating.count ? (
  <span className={`os-stars ${size}`} aria-label={`Rated ${rating.avg} out of 5 from ${rating.count} ${rating.count === 1 ? "rating" : "ratings"}`} data-testid={`menu-item-rating-${id}`}>{rating.avg} ★ <small>· {rating.count} rating{rating.count === 1 ? "" : "s"}</small></span>
) : null);

export const ItemCard = ({ it, qty, ctrl, testPrefix = "menu-item" }) => (
  <article className="os-card" data-testid={`${testPrefix}-${it.id}`}>
    <button type="button" className="os-card-photo" onClick={() => ctrl.onOpen(it)} aria-label={`View ${it.name}`} data-testid={`${testPrefix}-photo-${it.id}`}><QrMedia src={it.image_url} alt={it.name} /></button>
    <div className="os-card-body">
      <button type="button" className="os-card-name" onClick={() => ctrl.onOpen(it)} data-testid={`${testPrefix}-name-${it.id}`}><Veg veg={it.veg} /><span>{it.name}</span></button>
      <Stars rating={it.rating} id={it.id} />
      <div className="os-card-foot"><Price it={it} testId={`${testPrefix}-price-${it.id}`} /><AddControl it={it} qty={qty} {...ctrl} /></div>
    </div>
  </article>
);

/** Premium: one large card per row, whole dish visible (object-fit: contain). */
export const BigItemCard = ({ it, qty, ctrl }) => (
  <article className="os-big" data-testid={`menu-item-${it.id}`}>
    <button type="button" className="os-big-photo" onClick={() => ctrl.onOpen(it)} aria-label={`View ${it.name}`} data-testid={`menu-item-photo-${it.id}`}><QrMedia src={it.image_url} alt={it.name} size="large" /></button>
    <div className="os-big-body">
      <button type="button" className="os-big-name" onClick={() => ctrl.onOpen(it)} data-testid={`menu-item-name-${it.id}`}><Veg veg={it.veg} /><span>{it.name}</span></button>
      <Stars rating={it.rating} id={it.id} />
      {it.description && <p className="os-card-desc">{it.description}</p>}
      <div className="os-card-foot"><Price it={it} testId={`menu-item-price-${it.id}`} /><AddControl it={it} qty={qty} {...ctrl} /></div>
    </div>
  </article>
);
